import type {
  IBlockdaemonSDKError,
  IGetApiMetadataV2Request,
  IGetApiMetadataV2Response,
} from "@blockdaemon/bd-sdk";
import { isBlockdaemonSDKError } from "@blockdaemon/bd-sdk";
import { blockdaemonSDK } from "../../lib/sdk";
import { queryClient } from "../lib/query-client";

/**
 * Query key factory for SDK metadata
 */
export const metadataQueryKeys = {
  all: ["metadata"] as const,
  lists: () => [...metadataQueryKeys.all, "list"] as const,
  list: (filters: { category: string; endpoint: string }) =>
    [
      ...metadataQueryKeys.lists(),
      {
        category: filters.category,
        endpoint: filters.endpoint,
      },
    ] as const,
  details: () => [...metadataQueryKeys.all, "detail"] as const,
  detail: (params: { category: string; endpoint: string }) =>
    [
      ...metadataQueryKeys.details(),
      {
        category: params.category,
        endpoint: String(params.endpoint).toLowerCase().trim(),
      },
    ] as const,
};

/**
 * Shared query configuration for SDK metadata.
 * Kept minimal so it works for both hooks and prefetching.
 */
export const metadataQueryConfig = {
  staleTime: 30 * 60 * 1000, // 30 minutes
  gcTime: 30 * 60 * 1000, // 30 minutes
} as const;

/**
 * Fetcher for SDK metadata that throws on SDK errors
 */
export async function fetchSdkMetadata({
  category,
  endpoint,
}: IGetApiMetadataV2Request): Promise<IGetApiMetadataV2Response> {
  try {
    const response: IGetApiMetadataV2Response | IBlockdaemonSDKError =
      await blockdaemonSDK.defi.getApiMetadataV2({
        category,
        endpoint,
      });

    if (isBlockdaemonSDKError(response)) {
      throw new Error(response.detail || "Failed to fetch metadata");
    }

    return response;
  } catch (error) {
    throw error instanceof Error ? error : new Error("Failed to fetch metadata");
  }
}

/**
 * Prefetch metadata for both DEX and Bridge swap endpoints.
 */
export async function prefetchSdkSwapMetadata(): Promise<void> {
  await Promise.all([
    queryClient.prefetchQuery({
      queryKey: metadataQueryKeys.detail({ category: "dex", endpoint: "swap" }),
      queryFn: () => fetchSdkMetadata({ category: "dex", endpoint: "swap" }),
      ...metadataQueryConfig,
    }),
    queryClient.prefetchQuery({
      queryKey: metadataQueryKeys.detail({ category: "bridge", endpoint: "swap" }),
      queryFn: () => fetchSdkMetadata({ category: "bridge", endpoint: "swap" }),
      ...metadataQueryConfig,
    }),
  ]);
}
